import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Loading from 'Components/Atoms/Loading/Loading';
import Search from 'Components/Atoms/Search/Search';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import OutlineCalendar from 'Components/Atoms/OutlineIcons/OutlineCalendar';
import OutlineFilter from 'Components/Atoms/OutlineIcons/OutlineFilter';
import OutlineReconciliation from 'Components/Atoms/OutlineIcons/OutlineReconciliation';
import {
    PERIODS,
    CUSTOM_PERIOD,
    All_TIME_PERIOD,
    INVOICE_STATUS,
    INVOICE_VALID_FORMATS,
    INVOICE_TYPES,
} from 'Utils/constants';
import CardInvoiceAsync from './CardInvoiceAsync';
import { uploadInvoice } from 'Services/invoices';
import InputFile from 'Components/Atoms/InputFile/InputFile';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import { cn } from 'Utils/cn';
import { getReconciliationPendingAmount, getTransactionReconciliationAmount } from 'Utils/reconciliation';
import useInfiniteInvoices from 'Hooks/useInfiniteInvoices';
import useTransaction from 'Hooks/useTransaction';
import useInfiniteContacts from 'Hooks/useInfiniteContacts';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import useLocale from 'Hooks/useLocale';
import Select from 'Components/Atoms/Select/Select';
import Multiselect from 'Components/Molecules/Multiselect/Multiselect';
import MultiselectInfinite from 'Components/Molecules/MultiselectInfinite/MultiselectInfinite';
import CollapseContent from 'Components/Atoms/Collapse/CollapseContent';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import DateRangeCalendar from 'Components/Molecules/DateRangeCalendar/DateRangeCalendar';
import Input from 'Components/Atoms/Input/Input';
import Button from 'Components/Atoms/Button/Button';
import Icon from 'Components/Atoms/Icon/Icon';
import ProgressBar from 'Components/Atoms/ProgressBar/ProgressBar';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import { formatCurrency, formatDateISO } from 'Utils/formatters';
import ReconciliationAmountMismatchDialog from 'Pages/Reconciliation/ReconciliationAmountMismatchDialog';
import useReconciliationMismatchDialog from 'Hooks/useReconciliationMismatchDialog';
import useReconciliationMutation from 'Hooks/useReconciliationMutation';
import ReconciliationInvoiceItem from 'Components/Molecules/ReconciliationInvoiceItem/ReconciliationInvoiceItem';
import ReconciliationSelectedInvoices from 'Pages/Reconciliation/ReconciliationSelectedInvoices';
import InputFileButton from 'Components/Atoms/InputFileButton/InputFileButton';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const INVOICE_TYPE_ALL = 'all';

const DEFAULT_FILTERS = {
    period: All_TIME_PERIOD,
    dateFrom: null,
    dateTo: null,
    productsIds: null,
    contactsIds: null,
    amountFrom: '',
    amountTo: '',
};

export default function TransactionEditorDrawerInvoices(props) {
    const { transactionId } = props;

    const [query, setQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const sentinelRef = useRef(null);
    const initialSyncDone = useRef(false);

    const { t } = useTranslation('transactions');
    const { t: tR } = useTranslation('reconciliation');
    const locale = useLocale();

    const { transaction, reconcile, isReconciling } = useTransaction(transactionId);
    const { saveTransactionAssociations } = useReconciliationMutation();
    const queryClient = useQueryClient();
    const isReconciled = transaction?.is_reconciled;

    const [invoicesIds, setInvoicesIds] = useState([]);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    const [showExtraFilters, setShowExtraFilters] = useState(false);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [invoiceType, setInvoiceType] = useState(null);
    const [isCustomPeriodDialogOpen, setIsCustomPeriodDialogOpen] = useState(false);
    const [contactQuery, setContactQuery] = useState('');

    useEffect(() => {
        if (transaction && !initialSyncDone.current) {
            setInvoicesIds(transaction.invoices_ids ?? []);
            initialSyncDone.current = true;
        }
    }, [transaction]);

    const { visibleProvidersConnections } = useProvidersConnections();
    const {
        data: contactsData,
        isFetching: isFetchingContacts,
        fetchNextPage: fetchNextPageContacts,
        hasNextPage: hasNextPageContacts,
    } = useInfiniteContacts({ query: contactQuery });

    const {
        showMismatchDialog,
        closeMismatchDialog,
        handleMismatchReconcile,
        handleMismatchSave,
        checkAndReconcile,
        forceReconcile,
        itemType,
        mismatchAmount,
    } = useReconciliationMismatchDialog({ type: 'transaction' });

    const handleToggleInvoice = useCallback(
        (invoiceId) => {
            const next = invoicesIds.includes(invoiceId)
                ? invoicesIds.filter((id) => id !== invoiceId)
                : [...invoicesIds, invoiceId];
            setInvoicesIds(next);
            saveTransactionAssociations(transactionId, next);
        },
        [invoicesIds, saveTransactionAssociations, transactionId],
    );

    const handleDeleteInvoice = useCallback(
        (invoiceId) => {
            const next = invoicesIds.filter((id) => id !== invoiceId);
            setInvoicesIds(next);
            saveTransactionAssociations(transactionId, next);
        },
        [invoicesIds, saveTransactionAssociations, transactionId],
    );

    const handleUploadInvoice = async (fileList) => {
        const files = Array.from(fileList);
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadError(null);

        const uploadOne = async (file) => {
            try {
                const result = await uploadInvoice(file, { transactionId });
                setInvoicesIds((prev) => [...prev, result.id]);
                return result.id;
            } catch {
                return null;
            }
        };

        const results = await Promise.all(files.map(uploadOne));
        const someSucceeded = results.some(Boolean);
        const someFailed = results.some((id) => id === null);

        if (someSucceeded) {
            setInvoicesIds((latest) => {
                saveTransactionAssociations(transactionId, latest);
                return latest;
            });
            await queryClient.invalidateQueries({ queryKey: ['transactions', transactionId] });
        }

        if (someFailed) {
            setUploadError(t('could-not-upload-invoice'));
        }

        setIsUploading(false);
    };

    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const normalizedTransactionAmountForFilter = transaction?.amount_uc
        ? transaction.amount_uc * (transaction.reverse_sign ? -1 : 1)
        : null;
    const defaultInvoiceType =
        normalizedTransactionAmountForFilter === null
            ? null
            : normalizedTransactionAmountForFilter < 0
              ? INVOICE_TYPES.PURCHASE
              : INVOICE_TYPES.SALE;
    const effectiveInvoiceType = invoiceType ?? defaultInvoiceType;

    const invoiceTypeOptions = useMemo(
        () => [
            { label: tR('invoice-type.all'), value: INVOICE_TYPE_ALL },
            { label: tR('invoice-type.purchase'), value: INVOICE_TYPES.PURCHASE },
            { label: tR('invoice-type.sale'), value: INVOICE_TYPES.SALE },
        ],
        [tR],
    );

    const invoiceFilters = useMemo(() => {
        const base = {
            query: searchQuery || null,
            accounts_products_ids: filters.productsIds,
            contacts_ids: filters.contactsIds,
            amount_from: filters.amountFrom || null,
            amount_to: filters.amountTo || null,
            status: INVOICE_STATUS.RECONCILABLE,
            type: effectiveInvoiceType !== INVOICE_TYPE_ALL && effectiveInvoiceType ? effectiveInvoiceType : null,
        };
        if (filters.period === CUSTOM_PERIOD) {
            return {
                ...base,
                date_period: CUSTOM_PERIOD,
                date_from: filters.dateFrom ? formatDateISO(filters.dateFrom) : null,
                date_to: filters.dateTo ? formatDateISO(filters.dateTo) : null,
            };
        }
        return { ...base, date_period: filters.period };
    }, [searchQuery, filters, effectiveInvoiceType]);

    const { data, isFetching, fetchNextPage, hasNextPage, isLoading } = useInfiniteInvoices(invoiceFilters, {
        enabled: !isReconciled,
    });

    const invoices = data?.pages?.reduce((acc, page) => [...acc, ...page.items], []) ?? [];

    useEffect(() => {
        if (isFetching) return;

        if (sentinelRef.current) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            });

            observer.observe(sentinelRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [sentinelRef, isFetching]);

    useEffect(() => {
        if (uploadError) {
            const timeout = setTimeout(() => setUploadError(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [uploadError]);

    const productOptions = useMemo(() => {
        return visibleProvidersConnections.flatMap((connection) =>
            connection.products.map((product) => ({
                id: product.id,
                label: product.name,
                icon: <img src={connection.logo_url} height="18" width="18" alt="" />,
            })),
        );
    }, [visibleProvidersConnections]);

    const contactOptions = useMemo(() => {
        return (
            contactsData?.pages?.reduce((acc, page) => {
                return [
                    ...acc,
                    ...page.items.map((contact) => ({
                        id: contact.id,
                        label: contact.name,
                    })),
                ];
            }, []) ?? []
        );
    }, [contactsData]);

    const periodOptions = useMemo(() => {
        return PERIODS.flatMap((period) => {
            if (period.value === CUSTOM_PERIOD) {
                return [{ type: 'divider' }, { label: tR(period.i18n), value: period.value }];
            }
            return { label: tR(period.i18n), value: period.value };
        });
    }, [tR]);

    const periodLabel = useMemo(() => {
        if (filters.period === CUSTOM_PERIOD && filters.dateFrom && filters.dateTo) {
            const from = filters.dateFrom.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
            const to = filters.dateTo.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
            return `${from} - ${to}`;
        }
        const selectedPeriod = PERIODS.find((p) => p.value === filters.period);
        return selectedPeriod ? tR(selectedPeriod.i18n) : tR('periods.all-time');
    }, [filters.period, filters.dateFrom, filters.dateTo, tR, locale]);

    const productsLabel = useMemo(() => {
        if (!filters.productsIds || filters.productsIds.length === 0) return tR('products');
        const selected = productOptions.find((opt) => filters.productsIds.includes(opt.id));
        if (filters.productsIds.length === 1 && selected) return selected.label;
        return `${tR('products')} (${filters.productsIds.length})`;
    }, [filters.productsIds, productOptions, tR]);

    const contactsLabel = useMemo(() => {
        if (!filters.contactsIds || filters.contactsIds.length === 0) return tR('contacts');
        const selected = contactOptions.find((opt) => filters.contactsIds.includes(opt.id));
        if (filters.contactsIds.length === 1 && selected) return selected.label;
        return `${tR('contacts')} (${filters.contactsIds.length})`;
    }, [filters.contactsIds, contactOptions, tR]);

    const handleChangePeriod = (value) => {
        if (value === CUSTOM_PERIOD) {
            setIsCustomPeriodDialogOpen(true);
            return;
        }
        setFilter('period', value);
        setFilter('dateFrom', null);
        setFilter('dateTo', null);
    };

    const handleChangeCustomPeriod = (range) => {
        setIsCustomPeriodDialogOpen(false);
        if (!range) return;
        setFilter('period', CUSTOM_PERIOD);
        setFilter('dateFrom', range[0]);
        setFilter('dateTo', range[1]);
    };

    const normalizedTransactionAmount = getTransactionReconciliationAmount(transaction);
    const pendingAmount = getReconciliationPendingAmount(normalizedTransactionAmount, invoicesTotal);

    return (
        <>
            <ReconciliationAmountMismatchDialog
                open={showMismatchDialog}
                onClose={closeMismatchDialog}
                onReconcile={handleMismatchReconcile}
                onSave={handleMismatchSave}
                itemType={itemType}
                mismatchAmount={mismatchAmount}
                currency={transaction?.currency ?? 'EUR'}
            />

            {isReconciled && (
                <>
                    {transaction?.invoices_ids?.length === 0 && (
                        <div className="text-center text-gray-400 py-6 text-sm border border-gray-200 rounded-lg mt-3">
                            {tR('reconciled-without-invoices')}
                        </div>
                    )}
                    {transaction?.invoices_ids?.length > 0 && (
                        <>
                            <div className="flex items-center py-2 mt-4">
                                <div className="text-2xs uppercase tracking-wider font-semibold text-gray-500">
                                    {t('related-invoices')}
                                </div>
                            </div>
                            <div className="grid gap-2 mb-3">
                                {transaction.invoices_ids.map((invoiceId) => (
                                    <CardInvoiceAsync key={invoiceId} invoiceId={invoiceId} />
                                ))}
                            </div>
                        </>
                    )}
                    <div className="flex justify-center mt-4">
                        <Button
                            size="small"
                            variant="ghost"
                            disabled={isReconciling}
                            onClick={() =>
                                reconcile({
                                    invoicesIds: transaction?.invoices_ids,
                                    forceReconciliationStatus: false,
                                    forceTransactionsReconciliationStatus: false,
                                })
                            }
                        >
                            {tR('unreconcile')}
                        </Button>
                    </div>
                </>
            )}

            {!isReconciled && (
                <div className="pt-4">
                    <Tooltip
                        label={
                            <div className="grid gap-1.5 text-xs min-w-[180px] py-1">
                                <div className="flex justify-between gap-4">
                                    <span>{tR('total')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(transaction?.amount ?? 0, {
                                            currency: transaction?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-4">
                                    <span>{tR('reconciled-label')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(invoicesTotal, {
                                            currency: transaction?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                                <div
                                    className={cn('flex justify-between gap-4', pendingAmount !== 0 && 'text-warning')}
                                >
                                    <span>{pendingAmount < 0 ? tR('difference-label') : tR('pending-label')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(pendingAmount, {
                                            currency: transaction?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                            </div>
                        }
                    >
                        <div className="py-1 cursor-help">
                            <ProgressBar
                                value={Math.abs(invoicesTotal)}
                                max={Math.abs(normalizedTransactionAmount) || 1}
                                color={pendingAmount === 0 ? 'success' : pendingAmount < 0 ? 'danger' : 'warning'}
                                bottomLeftText={formatCurrency(invoicesTotal, {
                                    currency: transaction?.currency,
                                    locale,
                                })}
                                bottomRightText={`${pendingAmount < 0 ? tR('difference-label') : tR('pending-label')}: ${formatCurrency(pendingAmount, { currency: transaction?.currency, locale })}`}
                            />
                        </div>
                    </Tooltip>

                    <ReconciliationSelectedInvoices
                        invoicesIds={invoicesIds}
                        onDeleteInvoice={handleDeleteInvoice}
                        onChangeTotal={setInvoicesTotal}
                        showEmptyPlaceholder={false}
                    />

                    {invoicesIds.length === 0 && (
                        <div className="mt-3">
                            <InputFile
                                multiple
                                isBusy={isUploading}
                                label={t('select-or-upload-invoice')}
                                onUpload={handleUploadInvoice}
                                maxFileSize={MAX_FILE_SIZE}
                                acceptedFileTypes={INVOICE_VALID_FORMATS.join(',')}
                            />
                            {uploadError && <ErrorMessage className="mt-2" message={uploadError} />}
                        </div>
                    )}

                    <div className="flex flex-wrap mt-3 gap-2 mb-1">
                        {invoicesIds.length === 0 && (
                            <Button size="small" onClick={() => forceReconcile(transactionId)}>
                                <OutlineReconciliation size={16} color="currentColor" />
                                {tR('reconcile-without-invoice')}
                            </Button>
                        )}
                        {invoicesIds.length > 0 && (
                            <div className="w-full">
                                <div className="flex items-center justify-between gap-2">
                                    <Button
                                        size="small"
                                        variant={pendingAmount === 0 ? 'primary' : 'warning'}
                                        onClick={() => checkAndReconcile(transactionId, invoicesIds, pendingAmount)}
                                    >
                                        <Icon name="circle-check" color="currentColor" size={16} />
                                        {tR('reconcile')}
                                    </Button>

                                    <InputFileButton
                                        multiple
                                        size="small"
                                        isBusy={isUploading}
                                        onUpload={handleUploadInvoice}
                                        maxFileSize={MAX_FILE_SIZE}
                                        acceptedFileTypes={INVOICE_VALID_FORMATS.join(',')}
                                        iconSize={18}
                                        onError={setUploadError}
                                    >
                                        {t('upload-invoice')}
                                    </InputFileButton>
                                </div>
                                {uploadError && <ErrorMessage className="mt-2" message={uploadError} />}
                            </div>
                        )}
                    </div>

                    <div className="min-h-[240px] mt-3">
                        <div className="flex items-center gap-1 mb-2">
                            <Search
                                className="grow"
                                placeholder={tR('search-invoices')}
                                value={query}
                                onChange={setQuery}
                                onSearch={() => setSearchQuery(query)}
                                onClear={() => {
                                    setQuery('');
                                    setSearchQuery('');
                                }}
                            />
                            <IconButton
                                icon={showExtraFilters ? <Icon name="close" size={24} /> : <OutlineFilter />}
                                onClick={() => setShowExtraFilters((prev) => !prev)}
                                pressed={showExtraFilters}
                            />
                        </div>
                        <CollapseContent open={showExtraFilters}>
                            <div className="pt-2 pb-3 grid gap-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <Select
                                        size="small"
                                        className="w-full"
                                        options={periodOptions}
                                        icon={<OutlineCalendar className="text-dark-blue" />}
                                        onChange={handleChangePeriod}
                                        value={filters.period}
                                        placeholder={periodLabel}
                                        enableClear={filters.period !== DEFAULT_FILTERS.period}
                                        onClear={() => {
                                            setFilter('period', DEFAULT_FILTERS.period);
                                            setFilter('dateFrom', null);
                                            setFilter('dateTo', null);
                                        }}
                                    />
                                    <Select
                                        size="small"
                                        className="w-full"
                                        options={invoiceTypeOptions}
                                        value={effectiveInvoiceType ?? INVOICE_TYPE_ALL}
                                        onChange={(value) => setInvoiceType(value === INVOICE_TYPE_ALL ? null : value)}
                                        enableClear={invoiceType !== null}
                                        onClear={() => setInvoiceType(null)}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Multiselect
                                        size="small"
                                        className="w-full"
                                        label={productsLabel}
                                        options={productOptions}
                                        optionsSelected={filters.productsIds ?? []}
                                        onChangeSelected={(ids) =>
                                            setFilter('productsIds', ids?.length > 0 ? ids : null)
                                        }
                                        enableClear={filters.productsIds && filters.productsIds.length > 0}
                                        onClear={() => setFilter('productsIds', null)}
                                    />
                                    <MultiselectInfinite
                                        size="small"
                                        label={contactsLabel}
                                        options={contactOptions}
                                        optionsSelected={filters.contactsIds ?? []}
                                        onChangeSelected={(ids) =>
                                            setFilter('contactsIds', ids?.length > 0 ? ids : null)
                                        }
                                        query={contactQuery}
                                        onChangeQuery={setContactQuery}
                                        isFetching={isFetchingContacts}
                                        fetchNextPage={fetchNextPageContacts}
                                        hasNextPage={hasNextPageContacts}
                                        enableClear={filters.contactsIds && filters.contactsIds.length > 0}
                                        onClear={() => setFilter('contactsIds', null)}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Input
                                        type="number"
                                        placeholder={tR('from-placeholder')}
                                        value={filters.amountFrom}
                                        onChange={(ev) => setFilter('amountFrom', ev.target.value)}
                                        size="small"
                                    />
                                    <Input
                                        type="number"
                                        placeholder={tR('to-placeholder')}
                                        value={filters.amountTo}
                                        onChange={(ev) => setFilter('amountTo', ev.target.value)}
                                        size="small"
                                    />
                                </div>
                            </div>
                        </CollapseContent>

                        <Dialog open={isCustomPeriodDialogOpen} onClose={() => setIsCustomPeriodDialogOpen(false)}>
                            <DateRangeCalendar onChange={handleChangeCustomPeriod} />
                        </Dialog>

                        {isFetching && isLoading && (
                            <div className="text-center py-6">
                                <Loading />
                            </div>
                        )}

                        {!isLoading && (
                            <>
                                {invoices.length === 0 && (
                                    <div className="text-center text-gray-500 py-6 text-sm">
                                        {tR('no-invoices-found')}
                                    </div>
                                )}

                                {invoices.length > 0 && (
                                    <div className="grid border border-dark-blue/10 rounded-lg overflow-hidden mt-2">
                                        {invoices.map((invoice) => (
                                            <ReconciliationInvoiceItem
                                                key={invoice.id}
                                                transaction={transaction}
                                                invoice={invoice}
                                                checked={invoicesIds.includes(invoice.id)}
                                                onClick={() => handleToggleInvoice(invoice.id)}
                                            />
                                        ))}
                                    </div>
                                )}

                                <div className="mt-6 text-center text-gray-500 text-xs">
                                    {hasNextPage &&
                                        (isFetching ? (
                                            <Loading size="small" />
                                        ) : (
                                            <IconButton
                                                variant="ghost"
                                                icon={<OutlineArrowBottom />}
                                                onClick={fetchNextPage}
                                            />
                                        ))}
                                </div>

                                {hasNextPage && <div ref={sentinelRef} />}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

TransactionEditorDrawerInvoices.propTypes = {
    transactionId: PropTypes.string.isRequired,
};
